
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import style from '../styles/form.module.css'

import { getLayout, LayoutContext } from '../components/sg/Layout'
import Form, { Step } from '../components/Form'
import useTranslation from 'next-translate/useTranslation'
import classNames from 'classnames/bind'
import { useState, useEffect, useContext, useRef } from 'react'
import { getSortedCountries } from '../helpers/countries'
import { useRouter } from 'next/router'
import { defaultCurrency } from '../hooks/usePrices'
import useUser from '../hooks/useUser'
import IDUpload from '../components/IDUpload'

const countriesList = getSortedCountries()
export default function Upload () {
  const { t } = useTranslation('main')
  const c = classNames.bind(style)
  const { user, account, updateUser, mutateUser } = useUser()
  const router = useRouter()
  const { locale } = router
  const bankFormElem = useRef()
  const bankCountryElem = useRef()
  // eslint-disable-next-line no-unused-vars
  const [layoutContext, setLayoutContext] = useContext(LayoutContext)
  const [currentStep, setCurrentStep] = useState('loading')
  const [bank, setBank] = useState(account?.Treasuries?.find(t => t.Type === 'Bank' && t.AccNr))

  useEffect(() => {
    if (user && account && currentStep === 'loading') {
      const bank = account.Treasuries?.find(t => t.Type === 'Bank' && t.AccNr)
      setBank(bank)
      if (user.IsExpiringSoon || user.IsExpired) {
        setCurrentStep('upload')
      } else if (!account.HasBank) {
        setCurrentStep('bank')
      } else {
        router.push('/settings/account')
      }
    }
  }, [user])

  const handleBankSubmit = e => {
    e.preventDefault()
    setLayoutContext({})

    bankFormElem.current.removeAttribute('novalidate')
    if (bankFormElem.current.reportValidity()) {
      const bank = {
        Currency: account?.PreferredCurrency || defaultCurrency,
        Country: bankCountryElem.current.value
      }
      bankFormElem.current.querySelectorAll('input').forEach(elem => {
        bank[elem.id] = elem.value.trim()
      })
      setBank(bank)
      setCurrentStep('bankpreview')
    }
  }

  const handleBankConfirm = async () => {
    setCurrentStep('loading')
    const response = await updateUser(user, {
      Treasuries: account.Treasuries.filter(t => !(t.Type === 'Bank' && t.Currency === account.PreferredCurrency)).concat([Object.assign({ Type: 'Bank' }, bank)])
    })
    if (response.ok) {
      setLayoutContext({ success: t('upload-bank-success') })
      router.push('/portfolio')
    } else {
      setLayoutContext({ message: t('upload-unknownerror') })
      setCurrentStep('bank')
    }
  }

  const handleUploadSuccess = () => {
    setCurrentStep(bank ? 'success' : 'bank')
    mutateUser({ UserHasUploadedAnId: true })
  }

  const bankaccounttable = bank?.AccNr && (
    <table className={c('table')} cellSpacing={0}>
      <tbody>
        <tr>
          <td>{t('upload-bankname-label')}:</td>
          <td>{bank.AccBankName}</td>
        </tr>
        <tr>
          <td>{t('upload-bankswift-label')}:</td>
          <td>{bank.AccBankSwiftCode.toUpperCase()}</td>
        </tr>
        {bank.AccBankRoutingNumber ? (
          <tr>
            <td>{t('upload-bankrouting-label')}:</td>
            <td>{bank.AccBankRoutingNumber}</td>
          </tr>
        ) : null}
        {bank.AccBankOther ? (
          <tr>
            <td>{t('upload-bankother-label')}:</td>
            <td>{bank.AccBankOther}</td>
          </tr>
        ) : null}
        <tr>
          <td>{t('upload-bankaccountname-label')}:</td>
          <td>{bank.AccName}</td>
        </tr>
        <tr>
          <td>{t('upload-bankaccountnumber-label')}:</td>
          <td>{bank.AccNr}</td>
        </tr>
      </tbody>
    </table>
  )

  return (
    <Form
      title={t('upload-title')}
      onClose={currentStep !== 'loading' ? () => router.push('/portfolio') : undefined}
      step={currentStep}
    >
      <Step name='upload'>
        {user?.PassportExpiry && user.IsExpiringSoon
          ? (
            <>
              <h3>{t('upload-expiry-header')}</h3>
              <p>{t('upload-expiry-description')}</p>
            </>
          )
          : (
            <>
              <h3>{t('upload-upgrade-header')}</h3>
              <img src='/images/logo-icon-black.svg' width={50} height={50} style={{ float: 'right', margin: '.8em' }} />
              <p>
                {t('upload-upgrade-description')} <a href='https://silverbullion.com.sg' target='_blank' rel='noopener noreferrer'>silverbullion.com.sg</a>.
                <br />
                <br />
                {t('upload-upgrade-description2')}
              </p>
            </>
          )}
        <br />
        <IDUpload withBirthDate={!user?.BirthDate} onSubmit={() => setCurrentStep('loading')} onError={() => setCurrentStep('upload')} onSuccess={handleUploadSuccess} />
        <br />
        <br />
      </Step>
      <Step name='bank'>
        <form noValidate onSubmit={handleBankSubmit} ref={bankFormElem} className={c('cashwithdrawals')}>
          <h3 key={account?.PreferredCurrency}>{t('upload-bank-title', { currency: account?.PreferredCurrency })}</h3>
          {!bank?.Type ? (
            <>
              <p>{t('upload-bank-description')}</p>
              <div className={c('inputfield')}>
                <label htmlFor='bankname'>{t('upload-bankname-label')}</label>
                <select id='bankcountry' ref={bankCountryElem} key={account?.Addresses?.[0]?.Country} required defaultValue={bank?.Country || 'Singapore'}>
                  <option value='' disabled>{t('upload-bankcountry-placeholder')}</option>
                  {countriesList.map(country => <option key={country.code} value={country.name}>{country.name}</option>)}
                </select>
              </div>
              <div className={c('inputfield')}>
                <input type='text' id='AccBankName' placeholder={t('upload-bankname-placeholder')} required defaultValue={bank?.AccBankName} />
              </div>
              <div className={c('inputfield')}>
                <input type='text' id='AccBankSwiftCode' placeholder={t('upload-bankswift-placeholder')} required defaultValue={bank?.AccBankSwiftCode} />
                <div>
                  {locale.startsWith('de') && <a href='https://www.t-online.de/finanzen/geld-vorsorge/sparen-finanzieren/id_68615176/swift-code-was-ist-das-und-wo-finde-ich-ihn-.html' target='_blank' rel='noopener noreferrer nofollow'>{t('signup-passwordreadmore-link')}</a>}
                  {!locale.startsWith('de') && <a href='https://www.theswiftcodes.com/singapore/' target='_blank' rel='noopener noreferrer nofollow'>{t('upload-bankswift-lookup')}</a>}
                </div>
              </div>
              <div className={c('inputfield')}>
                <input type='text' id='AccBankRoutingNumber' placeholder={t('upload-bankrouting-placeholder')} defaultValue={bank?.AccBankRoutingNumber} />
              </div>
              <div className={c('inputfield')}>
                <input type='text' id='AccBankOther' placeholder={t('upload-bankother-placeholder')} defaultValue={bank?.AccBankOther} />
              </div>
              <div className={c('inputfield')}>
                <label htmlFor='AccName'>{t('upload-bankaccountname-label')}</label>
                <input type='text' id='AccName' placeholder={t('upload-bankaccountname-placeholder')} required defaultValue={bank?.AccName} />
              </div>
              <div className={c('inputfield')}>
                <input type='text' id='AccNr' placeholder={t('upload-bankaccountnumber-placeholder')} required defaultValue={bank?.AccNr} />
              </div>
              <br />
              <button type='submit'>{t('upload-bank-savebutton')}</button>
              <br />
            </>
          ) : bankaccounttable}
          <p className={c('note')}>
            <b>{t('upload-withdrawalverifiednote-title')}</b> {t('upload-withdrawalverifiednote')}
          </p>
        </form>
      </Step>
      <Step name='bankpreview'>
        <div className={c('bankpreview')}>
          <h3>{t('upload-bankverify-title')}</h3>
          <p>{t('upload-bankverify-info')}</p>
          {bankaccounttable}
          <br />
          <button className='pulsate' onClick={handleBankConfirm}>{t('upload-bank-confirmbutton')}</button>
          <br />
          <br />
        </div>
      </Step>
      <Step name='loading'>
        <div className={c('loading')}>
          <img src='/images/loader.svg' />
        </div>
      </Step>
      <Step name='success'>
        <div className={c('leadingsuccess', { success: currentStep === 'success' })}>
          <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 70 70'>
            <path fill='#D8D8D8' d='M35,60 C21.1928813,60 10,48.8071187 10,35 C10,21.1928813 21.1928813,10 35,10 C48.8071187,10 60,21.1928813 60,35 C60,48.8071187 48.8071187,60 35,60 Z M23.6332378,33.2260427 L22.3667622,34.7739573 L34.1433655,44.40936 L47.776114,27.6305926 L46.223886,26.3694074 L33.8566345,41.59064 L23.6332378,33.2260427 Z' />
            <circle stroke='#F6C358' cx='35' cy='35' r='24' strokeWidth='1.3' strokeLinecap='round' fill='transparent' />
            <polyline strong='#F6C358' strokeWidth='1.3' points='23 34 34 43 47 27' fill='transparent' />
          </svg>
        </div>
        <div>
          <h3>{t('upload-success-title')}</h3>
          <p>{t('upload-success-description')}</p>
        </div>
      </Step>
    </Form>
  )
}

Upload.requiresAuth = true
Upload.getLayout = getLayout


    export async function getStaticProps(ctx) {
        
        
        return {
          
          
          props: {
            
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/upload',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  